import classNames from "classnames";
import React from "react";

import styled from "styled-components";

export const ConsultationQuestions: React.FC<{}> = () => {
  const [currentSlide, setCurrentSlide] = React.useState<number>(0);

  const handleSlideChange = (dir: -1 | 1, totalSlides: number): void => {
    setCurrentSlide((prev) => (prev + dir + totalSlides) % totalSlides);
  };

  return (
    <ul className="accordion" id="accordionQuestions">
      {data.map(({ title, card }, idx) => (
        <StyledListItem key={idx} className="card">
          <StyledButton
            id={`heading-${idx}`}
            className={classNames("card-header", {
              collapsed: idx === 0,
            })}
            data-toggle="collapse"
            data-target={`#collapse-${idx}`}
            onClick={() => setCurrentSlide(0)}
          >
            <h2 className="h1 text-uppercase text-white mb-0">{title}</h2>
          </StyledButton>
          <ContentWrapper
            id={`collapse-${idx}`}
            className={classNames("collapse p-4", {
              show: idx === 0,
            })}
            aria-labelledby={`heading-${idx}`}
            data-parent="#accordionQuestions"
            totalSlides={card.length}
          >
            {card.map(({ title, subtitle, content }, idx) => (
              <StyledInner
                key={idx}
                slide={currentSlide}
                totalSlides={card.length}
              >
                <StyledContent>
                  {title && (
                    <h3 className="h4 text-uppercase text-center mb-3">
                      {title}
                    </h3>
                  )}
                  {subtitle && <strong>{subtitle}</strong>}
                  {content}
                </StyledContent>
              </StyledInner>
            ))}
            <Overlay totalSlides={card.length}>
              {card.length > 1 && currentSlide > 0 && (
                <PrevButton onClick={() => handleSlideChange(-1, card.length)}>
                  &#8249;
                </PrevButton>
              )}
              {card.length > 1 && currentSlide < card.length - 1 && (
                <NextButton onClick={() => handleSlideChange(1, card.length)}>
                  &#8250;
                </NextButton>
              )}
            </Overlay>
          </ContentWrapper>
        </StyledListItem>
      ))}
    </ul>
  );
};

const StyledListItem = styled.li`
  position: relative;
  background-color: transparent;
`;

const StyledButton = styled.button`
  border: none;
  &:focus {
    outline: none;
  }
`;

const ContentWrapper = styled.div<{ totalSlides: number }>`
  position: relative;
  width: ${(p) => p.totalSlides * 100}%;
  display: flex;
  overflow: hidden;
  background-color: ${(p) => p.theme.primary}cc;
`;

const StyledInner = styled.div<{ slide: number; totalSlides: number }>`
  position: relative;
  width: ${(p) => 100 / p.totalSlides}%;
  transition: all 1000ms ease;
  transform: translateX(${(p) => p.slide * -100}%);
`;

const Overlay = styled.div<{ totalSlides: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: calc(100% / ${(p) => p.totalSlides});
  z-index: 1;
`;

const StyledContent = styled.div`
  padding: 0.5rem 5rem;
`;

const NextButton = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(100% - 5rem);
  right: 0;
  width: 5rem;
  font-size: 60px;
  color: white;
  opacity: 0.5;
  background-color: transparent;
  border: none;
  &:focus {
    outline: none;
  }
`;

const PrevButton = styled(NextButton)`
  left: 0;
  right: calc(100% - 5rem);
`;

const List = styled.ol`
  margin: 0.5rem 0;
  list-style: upper-alpha;
  .highlight > span {
    padding: 2px;
    color: ${p => p.theme.dark};
    background-color: ${p => p.theme.gold};
  }
`;
interface Data {
  title: string;
  card: Card[];
}

interface Card {
  title: string;
  subtitle: string;
  content?: JSX.Element;
}

const data: Data[] = [
  {
    title: "Physical inactivity",
    card: [
      {
        title: "Awareness",
        subtitle:
          "What is the minimum amount of moderate intensity* activity that you need to accumulate each week for good health?",
        content: (
          <>
            <List>
              <li>None</li>
              <li>At least 60 minutes</li>
              <li className="highlight"><span>At least 150 minutes</span></li>
              <li>At least 400 minutes</li>
            </List>
            <p>
              *Moderate intensity exercise will cause a slight, but noticeable
              increase in your breathing and heart rate.
            </p>
          </>
        ),
      },
      {
        title: "Awareness",
        subtitle:
          "How many times a week do you need to do muscle strengthening activities for good health?",
        content: (
          <List>
            <li>None</li>
            <li>At least 1 day</li>
            <li className="highlight"><span>At least 2 days</span></li>
            <li>At least 3 days</li>
          </List>
        ),
      },
      {
        title: "Awareness",
        subtitle: "Sitting down for long periods of time without breaks is bad for your health.",
        content: (
          <List>
            <li className="highlight"><span>True</span></li>
            <li>False</li>
          </List>
        ),
      },
      {
        title: "Behaviour",
        subtitle:
          "How many days last week did you exercise at a moderate intensity* for at least 30 minutes?",
        content: (
          <>
            <List>
              <li>None</li>
              <li>1</li>
              <li>2</li>
              <li>3</li>
              <li>4</li>
              <li>5</li>
              <li>6</li>
              <li>7</li>
            </List>
            <p>
              *Moderate intensity exercise will cause a slight, but noticeable
              increase in your breathing and heart rate.
            </p>
          </>
        ),
      },
      {
        title: "Behaviour",
        subtitle:
          "Which best describes your sedentary time throughout the working day?",
        content: (
          <List>
            <li>I spend most of the day sitting</li>
            <li>
              I spend most of the day sitting, but regularly interrupt my
              sitting (for at least 2-4 minutes every hour)
            </li>
            <li>I spend most of the day standing</li>
          </List>
        ),
      },
    ],
  },
  {
    title: "Unhealthy eating and drinking",
    card: [
      {
        title: "Awareness",
        subtitle:
          "How many serves of fruit* do you need to eat each day for good health?",
        content: (
          <>
            <List>
              <li>Less than 1 serve per day</li>
              <li>1 serve per day</li>
              <li className="highlight"><span>2 serves per day</span></li>
              <li>3 or more serves per day</li>
            </List>
            <p>
              *A serve of fruit is a medium piece or 2 small pieces of fresh
              fruit, or one cup of chopped or canned fruit.
            </p>
          </>
        ),
      },
      {
        title: "Awareness",
        subtitle:
          "How many serves of vegetables** do you need to eat each day for good health?",
        content: (
          <>
            <List>
              <li>Less than 1 serve per day</li>
              <li>1 serve per day</li>
              <li>2 serves per day</li>
              <li>4 serves per day</li>
              <li className="highlight"><span>5 or more serves per day</span></li>
            </List>
            <p>
              **A serve of vegetables is ½ a cup of cooked vegetables (fresh,
              frozen or tinned) or 1 cup of salad.
            </p>
          </>
        ),
      },
      {
        title: "Attitudes / Behaviour",
        subtitle: "Which category best describes you?",
        content: (
          <List>
            <li>
              Overall, my diet is not healthy and I do not intend to eat
              healthier in the next 6 months
            </li>
            <li>
              Overall, my diet is not healthy, but I am thinking about starting
              to eat healthier in the next 6 months
            </li>
            <li>
              Overall, my diet is healthy, but it has only become so within the last 6 months 
            </li>
            <li>
              Overall, my diet is healthy, and has been so for longer than 6
              months
            </li>
          </List>
        ),
      },
      {
        title: "Behaviour",
        subtitle: "How many serves of fruit* do you usually eat per day?",
        content: (
          <>
            <List>
              <li>Less than 1 serve per day</li>
              <li>1 serve per day</li>
              <li>2 serves per day</li>
              <li>3 or more serves per day</li>
            </List>
            <p>
              *A serve of fruit is a medium piece or 2 small pieces of fresh
              fruit, or one cup of chopped or canned fruit.
            </p>
          </>
        ),
      },
      {
        title: "Behaviour",
        subtitle:
          "How many serves of vegetables** or salad do you usually eat per day?",
        content: (
          <>
            <List>
              <li>Less than 1 serve per day</li>
              <li>1 serve per day</li>
              <li>2 serves per day</li>
              <li>4 serves per day</li>
              <li>5 or more serves per day</li>
            </List>
            <p>
              **A serve of vegetables is ½ a cup of cooked vegetables (fresh,
              frozen or tinned) or 1 cup of salad.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "Smoking",
    card: [
      {
        title: "Attitudes / behaviour",
        subtitle: "Which category best describes you?",
        content: (
          <List>
            <li>I smoke regularly with no intention to stop</li>
            <li>I smoke regularly but plan to stop</li>
            <li>I smoke occasionally with no intention to stop</li>
            <li>I smoke occasionally but plan to stop</li>
            <li>I don’t smoke now</li>
            <li>I’ve never smoked</li>
          </List>
        ),
      },
    ],
  },
  {
    title: "Alcohol use",
    card: [
      {
        title: "Awareness",
        subtitle:
          "In healthy adults, how many standard drinks* on any one day will lower your risk of harm from alcohol-related injury?",
        content: (
          <>
            <List>
              <li>No more than 2</li>
              <li>No more than 3</li>
              <li className="highlight"><span>No more than 4</span></li>
              <li>No more than 5</li>
            </List>
            <p>
              *A standard drink is equal to 30mL (1 nip) of spirits, 100mL of wine, a can or stubbie of mid-strength beer or one middy of full-strength beer.
            </p>
          </>
        ),
      },
      {
        title: "Awareness",
        subtitle:
          "To reduce the risk of harm from alcohol-related disease, healthy adults should limit their standard drinks* to:",
        content: (
          <>
            <List>
              <li>8 standard drinks per week</li>
              <li className="highlight"><span>10 standard drinks per week</span></li>
              <li>12 standard drinks per week</li>
              <li>14 standard drinks per week</li>
            </List>
            <p>
              *A standard drink is equal to 30mL (1 nip) of spirits, 100mL of wine, a can or stubbie of mid-strength beer or one middy of full-strength beer.
            </p>
          </>
        ),
      },
      {
        title: "Attitudes / Behaviour",
        subtitle: "Which category best describes you?",
        content: (
          <List>
            <li>
              Overall, my alcohol consumption is not healthy, and I do not
              intend to drink less alcohol in the next 6 months
            </li>
            <li>
              Overall, my alcohol consumption is not healthy, but I am thinking
              about starting to drink less alcohol in the next 6 months
            </li>
            <li>
              Overall, my levels of alcohol consumption are healthy, but
              occasionally I drink too much
            </li>
            <li>
              Overall, my levels of alcohol consumption are healthy, but it has
              only become so within the last 6 months
            </li>
            <li>
              Overall, my levels of alcohol consumption are healthy, and this
              has been so for longer than 6 months
            </li>
          </List>
        ),
      },
      {
        title: "Behaviour",
        subtitle: "How often do you have a drink containing alcohol?",
        content: (
          <List>
            <li>Never</li>
            <li>Monthly or less</li>
            <li>Monthly</li>
            <li>Weekly</li>
            <li>Daily or almost daily</li>
          </List>
        ),
      },
      {
        title: "Behaviour",
        subtitle:
          "How many standard drinks* do you have on a typical day when you are drinking?",
        content: (
          <>
            <List>
              <li>1-2</li>
              <li>3-4</li>
              <li>5-6</li>
              <li>7-8</li>
              <li>9-10</li>
              <li>More than 10</li>
            </List>
            <p>
              *A standard drink is equal to 30mL (1 nip) of spirits, 100mL of wine, a can or stubbie of mid-strength beer or one middy of full-strength beer.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "General questions",
    card: [
      {
        title: "",
        subtitle: "",
        content: (
          <>
            <strong>
              In addition to assessing staff awareness levels, attitudes and
              behaviours it is important to consider:
            </strong>
            <ul className="bullet-fulcrum bullet-gold mb-4">
              <li>What employees want from the program.</li>
              <li>What may stop them from participating in the program.</li>
              <li>What would encourage them to get involved in the program.</li>
            </ul>
            <strong>Some example questions are shown below:</strong>
            <ul className="bullet-fulcrum bullet-gold mb-4">
              <li>
                What strategies would you like to see implemented in the
                workplace (think about policies, infrastructure, facilities,
                education and activities)?
              </li>
              <li>
                What would assist you in making healthier choices at work?
              </li>
              <li>What is currently supporting you to be healthy at work?</li>
              <li>
                Are there any barriers preventing you from being healthy at
                work?
              </li>
              <li>
                Do you think there is adequate infrastructure and facilities to
                support you to be healthy at work? If no, what could be
                improved?
              </li>
              <li>
                What would encourage you to participate in a workplace health
                and wellbeing program?
              </li>
              <li>
                What motivates you to make healthy choices (e.g. improved
                health, family role model)?
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
];
